import React from 'react';
import { ramOptions, cpuOptions, diskOptions, osOptions, mariadbVersionOptions, apacheVersionOptions, nodejsVersionOptions } from '../data/predefinedValues';
import { serverTypes } from '../utils/serverModel';

const ServerForm = ({ 
  server, 
  index, 
  env,
  onChange,
  onAddLocalModification,
  onLocalModificationChange,
  onRemoveLocalModification,
  showLocalModifications,
  onDuplicate,
  onRemove,
  canRemove = true
}) => {
  
  // Helper pour créer un champ select avec option custom
  const renderSelectField = (label, name, options, value, customValue) => {
    const isCustom = value === 'custom';
    
    return (
      <div>
        <label className="block text-gray-700 mb-1 text-sm">{label}</label>
        <select
          name={name}
          className="w-full p-2 border border-gray-300 rounded text-sm"
          value={value}
          onChange={(e) => onChange(env, index, e)}
        >
          <option value="">Sélectionnez {label.toLowerCase()}</option>
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
          <option value="custom">Autre (personnalisé)</option>
        </select>
        {isCustom && (
          <input
            type="text"
            className="w-full mt-1 p-2 border border-gray-300 rounded text-sm"
            placeholder={`Spécifiez ${label.toLowerCase()}`}
            value={customValue || ''}
            onChange={(e) => onChange(env, index, { 
              target: { name: `custom${name.charAt(0).toUpperCase() + name.slice(1)}`, value: e.target.value } 
            })}
          />
        )}
      </div>
    );
  };

  // Helper pour créer un champ avec bouton modification
  const renderGitVersionField = (label, name, component) => {
    if (server.type === 'Serveur base de données') return null;
    if (server.type === 'Serveur applicatif' && component === 'marshallerInterop') return null;
    if (server.type === 'Serveur interop' && (component === 'patientApp' || component === 'managerFrontApp')) return null;
    
    return (
      <div>
        <label className="block text-gray-700 mb-1 text-sm">{label}</label>
        <div className="flex space-x-2">
          <input
            type="text"
            name={name}
            className="flex-1 p-2 border border-gray-300 rounded text-sm"
            value={server[name]}
            onChange={(e) => onChange(env, index, e)}
          />
          <button 
            type="button"
            className="bg-amber-500 text-white px-2 py-1 rounded text-xs hover:bg-amber-600"
            onClick={() => onAddLocalModification(env, index, component)}
          >
            + Modif
          </button>
        </div>
      </div>
    );
  };

  // Helper pour les modifications locales
  const renderLocalModifications = (component) => {
    const key = `${env}-${index}-${component}`;
    if (!showLocalModifications[key] || !server.localModifications?.[component]) return null;

    return (
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
        <h4 className="font-semibold text-sm mb-2">Modifications locales {component}</h4>
        {server.localModifications[component].map((mod, modIndex) => (
          <div key={mod.id} className="mb-3 pb-3 border-b border-amber-200">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold">Modification #{modIndex + 1}</span>
              <button 
                type="button" 
                className="text-red-500 text-xs"
                onClick={() => onRemoveLocalModification(env, index, component, modIndex)}
              >
                Supprimer
              </button>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Nom du fichier"
                className="w-full p-1 text-xs border border-amber-200 rounded"
                value={mod.filename}
                onChange={(e) => onLocalModificationChange(env, index, component, modIndex, 'filename', e.target.value)}
              />
              <input
                type="text"
                placeholder="Description brève"
                className="w-full p-1 text-xs border border-amber-200 rounded"
                value={mod.description}
                onChange={(e) => onLocalModificationChange(env, index, component, modIndex, 'description', e.target.value)}
              />
              <textarea
                placeholder="Code modifié"
                rows="3"
                className="w-full p-1 text-xs border border-amber-200 rounded font-mono"
                value={mod.code}
                onChange={(e) => onLocalModificationChange(env, index, component, modIndex, 'code', e.target.value)}
              ></textarea>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Conditions d'affichage selon le type de serveur
  const showMariaDB = server.type !== 'Serveur applicatif' && server.type !== 'Serveur interop';
  const showApache = server.type !== 'Serveur base de données';

  return (
    <div className="mb-6 p-4 rounded border" style={{ backgroundColor: `${server.color}15`, borderColor: server.color }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h3 className="font-semibold" style={{ color: server.color }}>Serveur {index + 1}</h3>
          <span className="ml-2 px-2 py-1 text-xs rounded" style={{ backgroundColor: server.color, color: 'white' }}>
            {server.type}
          </span>
        </div>
        <div className="flex space-x-2">
          {onDuplicate && (
            <button
              type="button"
              onClick={() => onDuplicate(env, index)}
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
            >
              Dupliquer
            </button>
          )}
          <button
            type="button"
            onClick={() => onRemove(env, index)}
            className="text-red-600 text-sm hover:text-red-800"
            disabled={!canRemove}
          >
            Supprimer
          </button>
        </div>
      </div>
      
      {/* Champs du formulaire */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Informations de base */}
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Nom du serveur</label>
          <input
            type="text"
            name="name"
            className="w-full p-2 border border-gray-300 rounded text-sm"
            value={server.name}
            onChange={(e) => onChange(env, index, e)}
          />
        </div>
        
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Type de serveur</label>
          <select
            name="type"
            className="w-full p-2 border border-gray-300 rounded text-sm"
            value={server.type}
            onChange={(e) => onChange(env, index, e)}
          >
            {serverTypes.map(type => (
              <option key={type.value} value={type.value}>{type.value}</option>
            ))}
          </select>
        </div>
        
        {/* Champs avec options custom */}
        {renderSelectField('OS', 'os', osOptions, server.os, server.customOs)}
        {renderSelectField('RAM', 'ram', ramOptions, server.ram, server.customRam)}
        {renderSelectField('CPU', 'cpu', cpuOptions, server.cpu, server.customCpu)}
        {renderSelectField('Espace disque', 'diskSpace', diskOptions, server.diskSpace, server.customDiskSpace)}
        
        {/* Dossier d'installation APP */}
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Dossier d'installation APP</label>
          <input
            type="text"
            name="appInstallFolder"
            className="w-full p-2 border border-gray-300 rounded text-sm"
            value={server.appInstallFolder}
            onChange={(e) => onChange(env, index, e)}
          />
        </div>
        
        {/* MariaDB - conditionnel */}
        {showMariaDB && renderSelectField('Version MariaDB', 'mariadbVersion', mariadbVersionOptions, server.mariadbVersion, server.customMariadbVersion)}
        
        {showMariaDB && (
          <div>
            <label className="block text-gray-700 mb-1 text-sm">Dossier d'installation BDD</label>
            <input
              type="text"
              name="dbInstallFolder"
              className="w-full p-2 border border-gray-300 rounded text-sm"
              value={server.dbInstallFolder}
              onChange={(e) => onChange(env, index, e)}
            />
          </div>
        )}
        
        {showMariaDB && (
          <div>
            <label className="block text-gray-700 mb-1 text-sm">BDD optimisée</label>
            <div className="pt-2 space-y-1">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`dbOptimized-${index}`}
                  className="mr-2"
                  checked={server.dbOptimized === undefined}
                  onChange={() => onChange(env, index, { target: { name: 'dbOptimized', value: undefined } })}
                />
                <span className="text-gray-500 text-sm">Non défini</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`dbOptimized-${index}`}
                  className="mr-2"
                  checked={server.dbOptimized === true}
                  onChange={() => onChange(env, index, { target: { name: 'dbOptimized', value: true } })}
                />
                <span className="text-green-600 text-sm">Oui</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`dbOptimized-${index}`}
                  className="mr-2"
                  checked={server.dbOptimized === false}
                  onChange={() => onChange(env, index, { target: { name: 'dbOptimized', value: false } })}
                />
                <span className="text-red-600 text-sm">Non</span>
              </label>
            </div>
          </div>
        )}
        
        {/* Apache - conditionnel */}
        {showApache && renderSelectField('Version Apache', 'apacheVersion', apacheVersionOptions, server.apacheVersion, server.customApacheVersion)}
        
        {/* NodeJS - toujours affiché */}
        {renderSelectField('Version NodeJS', 'nodejsVersion', nodejsVersionOptions, server.nodejsVersion, server.customNodejsVersion)}
        
        {/* Versions Git */}
        {renderGitVersionField('Version Git patient-app', 'gitPatientAppVersion', 'patientApp')}
        {renderGitVersionField('Version Git manager-front-app', 'gitManagerFrontAppVersion', 'managerFrontApp')}
        {renderGitVersionField('Version Git marshaller', 'gitMarshallerVersion', 'marshaller')}
        {server.type === 'Serveur interop' && renderGitVersionField('Version Git marshaller-interop', 'gitMarshallerInteropVersion', 'marshallerInterop')}
        
        {/* Sonde déployée */}
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Sonde déployée</label>
          <div className="pt-2 space-y-1">
            <label className="flex items-center">
              <input
                type="radio"
                name={`probeDeployed-${index}`}
                className="mr-2"
                checked={server.probeDeployed === undefined}
                onChange={() => onChange(env, index, { target: { name: 'probeDeployed', value: undefined } })}
              />
              <span className="text-gray-500 text-sm">Non défini</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`probeDeployed-${index}`}
                className="mr-2"
                checked={server.probeDeployed === true}
                onChange={() => onChange(env, index, { target: { name: 'probeDeployed', value: true } })}
              />
              <span className="text-green-600 text-sm">Oui</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`probeDeployed-${index}`}
                className="mr-2"
                checked={server.probeDeployed === false}
                onChange={() => onChange(env, index, { target: { name: 'probeDeployed', value: false } })}
              />
              <span className="text-red-600 text-sm">Non</span>
            </label>
          </div>
        </div>
        
        {/* NOUVEAU: Accès SSH */}
        <div>
          <label className="block text-gray-700 mb-1 text-sm">Accès SSH direct</label>
          <div className="pt-2">
            <input
              type="checkbox"
              name="sshAccessible"
              className="mr-2"
              checked={server.sshAccessible || false}
              onChange={(e) => onChange(env, index, e)}
            />
            <label className="text-xs text-gray-600">Accessible sans bastion/VM graphique</label>
          </div>
        </div>
        
        {/* NOUVEAU: Utilisateur SSH - conditionnel */}
        {server.sshAccessible && (
          <div>
            <label className="block text-gray-700 mb-1 text-sm">Utilisateur SSH</label>
            <input
              type="text"
              name="sshUsername"
              className="w-full p-2 border border-gray-300 rounded text-sm"
              value={server.sshUsername || 'root'}
              onChange={(e) => onChange(env, index, e)}
              placeholder="root"
            />
          </div>
        )}
        
        {/* NOUVEAU: Port SSH - conditionnel */}
        {server.sshAccessible && (
          <div>
            <label className="block text-gray-700 mb-1 text-sm">Port SSH</label>
            <input
              type="text"
              name="sshPort"
              className="w-full p-2 border border-gray-300 rounded text-sm"
              value={server.sshPort || '22'}
              onChange={(e) => onChange(env, index, e)}
              placeholder="22"
            />
          </div>
        )}
      </div>
      
      {/* Zones de modifications locales */}
      {renderLocalModifications('patientApp')}
      {renderLocalModifications('managerFrontApp')}
      {renderLocalModifications('marshaller')}
      {renderLocalModifications('marshallerInterop')}
    </div>
  );
};

export default ServerForm;
